import React, { useCallback, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { TipOption, DEFAULT_TIP_OPTIONS } from '@oolio-group/domain';
import { useCurrency, useTranslation } from '@oolio-group/localization';
import styles from './TipSelectionModal.styles';

export interface TipSelectionModalProps {
  amount: number;
  tipOptions?: TipOption[];
  onContinue: (tipAmount: number) => void;
  onSkip: () => void;
  onClose: () => void;
}

const CUSTOM_INDEX = -1;
const KEYPAD_KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['.', '0', '⌫'],
];

const TipSelectionModal: React.FC<TipSelectionModalProps> = ({
  amount,
  tipOptions,
  onContinue,
  onSkip,
  onClose,
}) => {
  const { formatCurrency, currencySymbol } = useCurrency();
  const { translate } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState(0);
  const [showKeypad, setShowKeypad] = useState(false);
  const [keypadValue, setKeypadValue] = useState('');

  const options = useMemo(
    () =>
      tipOptions && tipOptions.length > 0 ? tipOptions : DEFAULT_TIP_OPTIONS,
    [tipOptions],
  );

  const calculateTipAmount = useCallback(
    (option: TipOption) => {
      return Number(((amount * option.value) / 100).toFixed(2));
    },
    [amount],
  );

  const selectedTipAmount = useMemo(() => {
    if (selectedIndex === null) return 0;
    if (selectedIndex === CUSTOM_INDEX) return customAmount;
    return calculateTipAmount(options[selectedIndex]);
  }, [selectedIndex, customAmount, calculateTipAmount, options]);

  const totalWithTip = useMemo(
    () => Number((amount + selectedTipAmount).toFixed(2)),
    [amount, selectedTipAmount],
  );

  const onPressContinue = useCallback(() => {
    onContinue(selectedTipAmount);
  }, [onContinue, selectedTipAmount]);

  const onKeypadPress = useCallback((key: string) => {
    setKeypadValue(prev => {
      if (key === '⌫') return prev.slice(0, -1);
      if (key === '.' && prev.includes('.')) return prev;
      const decimals = prev.split('.')[1];
      if (decimals && decimals.length >= 2) return prev;
      return prev + key;
    });
  }, []);

  const onKeypadConfirm = useCallback(() => {
    const value = parseFloat(keypadValue) || 0;
    setCustomAmount(value);
    setSelectedIndex(CUSTOM_INDEX);
    setShowKeypad(false);
    setKeypadValue('');
  }, [keypadValue]);

  const onKeypadDismiss = useCallback(() => {
    setShowKeypad(false);
    setKeypadValue('');
  }, []);

  if (showKeypad) {
    return (
      <View style={styles.keypadOverlay}>
        <View style={styles.keypadContainer}>
          <Text style={styles.keypadTitle}>
            {translate('payment.tip.customTipAmount')}
          </Text>
          <Text style={styles.keypadDisplay}>
            {`${currencySymbol ?? ''}${keypadValue || '0'}`}
          </Text>
          <View style={styles.keypadGrid}>
            {KEYPAD_KEYS.map((row, rowIdx) => (
              <View key={rowIdx} style={styles.keypadRow}>
                {row.map(key => (
                  <TouchableOpacity
                    key={key}
                    style={styles.keypadBtn}
                    onPress={() => onKeypadPress(key)}
                  >
                    <Text style={styles.keypadBtnText}>{key}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            ))}
            <View style={styles.keypadRow}>
              <TouchableOpacity
                style={styles.keypadCancelBtn}
                onPress={onKeypadDismiss}
              >
                <Text style={styles.keypadBtnText}>
                  {translate('button.cancel')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.keypadConfirmBtn}
                onPress={onKeypadConfirm}
              >
                <Text style={styles.keypadBtnText}>
                  {translate('button.confirm')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.overlay}>
      <TouchableOpacity
        testID="btn-tip-close"
        style={styles.closeBtn}
        onPress={onClose}
      >
        <Text style={styles.closeBtnText}>✕</Text>
      </TouchableOpacity>

      <View style={styles.body}>
        <Text style={styles.totalLabel}>
          {translate('payment.tip.totalAmount')}
        </Text>
        <Text style={styles.totalAmount}>{formatCurrency(amount)}</Text>

        <Text style={styles.heading}>{translate('payment.tip.addTip')}</Text>

        <View style={styles.cardsRow}>
          {options.map((option, index) => {
            const isSelected = selectedIndex === index;
            const tipAmount = calculateTipAmount(option);
            return (
              <TouchableOpacity
                key={index}
                testID={`tip-option-${index}`}
                style={[styles.card, isSelected && styles.cardSelected]}
                onPress={() => setSelectedIndex(index)}
              >
                <Text
                  style={[
                    styles.cardLabel,
                    isSelected && styles.cardLabelSelected,
                  ]}
                >
                  {`${option.name} (${option.value}%)`}
                </Text>
                <Text
                  style={[
                    styles.cardAmount,
                    isSelected && styles.cardAmountSelected,
                  ]}
                >
                  {formatCurrency(tipAmount)}
                </Text>
              </TouchableOpacity>
            );
          })}
          <TouchableOpacity
            testID="tip-option-custom"
            style={[
              styles.card,
              selectedIndex === CUSTOM_INDEX && styles.cardSelected,
            ]}
            onPress={() => setShowKeypad(true)}
          >
            <Text
              style={[
                styles.cardLabel,
                selectedIndex === CUSTOM_INDEX && styles.cardLabelSelected,
              ]}
            >
              {translate('payment.tip.custom')}
            </Text>
            <Text
              style={[
                styles.cardAmount,
                selectedIndex === CUSTOM_INDEX && styles.cardAmountSelected,
              ]}
            >
              {formatCurrency(customAmount)}
            </Text>
          </TouchableOpacity>
        </View>

        <View style={styles.actions}>
          <TouchableOpacity
            testID="btn-tip-confirm"
            style={styles.confirmBtn}
            onPress={onPressContinue}
          >
            <Text style={styles.confirmBtnText}>
              {translate('payment.tip.confirm', {
                amount: formatCurrency(totalWithTip),
              })}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            testID="btn-tip-skip"
            style={styles.skipBtn}
            onPress={onSkip}
          >
            <Text style={styles.skipBtnText}>
              {translate('payment.tip.maybeNextTime')}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

export default TipSelectionModal;
